import React, { useCallback, useState } from 'react';
import HandwritingApp from '../../../handwriting/App';
import { completeSession, saveProgress } from '../../api/activitySessions';

/**
 * HandWritingSession - Wrapper for HandwritingApp that handles session completion.
 * Reads session_index from initial_snapshot (frozen at session creation) and
 * required_score from activity settings to build a deterministic letter/difficulty
 * sequence for this session.
 */
export const HandWritingSession = ({
  session,
  activity,
  onComplete
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sessionIndex = Number(session.initial_snapshot?.session_index ?? 0);
  const requiredScore = Number(activity.required_score ?? 3);
  const lastProgress = session.final_snapshot || null;

  const handleGameComplete = useCallback(async (gameResult) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const finalSnapshot = {
        completed: gameResult.completed,
        total: gameResult.total
      };

      const result = await completeSession(session.id, finalSnapshot);

      if (result.status === 'success') {
        onComplete(result);
      } else {
        console.error('Unexpected completion status:', result.status);
        alert(result.message || 'Failed to complete activity');
      }
    } catch (error) {
      console.error('Error completing session:', error);
      alert(error.message || 'Failed to complete activity');
    } finally {
      setIsSubmitting(false);
    }
  }, [session.id, onComplete, isSubmitting]);

  const handleProgressSave = useCallback(async (progressData) => {
    try {
      await saveProgress(session.id, progressData);
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  }, [session.id]);

  return (
    <HandwritingApp
      railsEnv={window.railsEnv || 'development'}
      lastProgress={lastProgress}
      sessionIndex={sessionIndex}
      requiredScore={requiredScore}
      onComplete={handleGameComplete}
      onProgressSave={handleProgressSave}
      debugging={activity.debugging || false}
    />
  );
};
